/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import descriptors from '../../locales/en-US/descriptors';
import IEnergyManagementProps, {
  IAny,
  IRequestContext,
  ISiteCard
} from '../DashboardKPIWidgets/IEnergyManagementProps';
import useMounted from '../shared/useMount/useMount';
import useConnectorAndDynamicProps from '../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import {
  getUtilityTypeCount,
  getUtilityTypeDetails,
  isObjectOrArrayAndEmpty
} from '../shared/Utils/Utils';
import { Card, Icon, Notification } from '@scuf/common';
import withLoadingStatus from '../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../shared/StatusWrappers/ErrorStatus';
import ConnectionError from '../shared/ErrorComponents/ConnectionError';
import './UtilityServiceSettingsCommon.scss';
import UtilityServiceAccountTable from './UtilityServiceAccountTable';
import UtilityServiceModal from '../UtilityServiceModal/UtilityServiceModal';
import { useUtilityAccountContext } from '../../context/UtilityAccountContext/UtilityAccountContext';
import UtilityAccountAction from '../../context/UtilityAccountContext/UtilityAccountActions';
import { useTranslation } from '@scuf/localization';
import DeactivateAccount from './DeactivateAccount/DeactivateAccount';
import Reverify from './Reverify/Reverify';
import { apiNoContentStatusCode } from '../shared/Utils/Constants';
import { IAccountStatus } from './IUtilityServiceSettingsProps';
import MeterNameChange from './MeterNameChange/MeterNameChange';
import UpdatedUtilityServiceModal from '../UtilityServiceModal/UpdatedUtilityServiceModal';
import UpdatedUtilityServiceAccountTable from './UpdatedUtilityServiceAccountTable';
import ConvertIntrgrationModal from './ConvertIntrgrationModal';
import { ApiContext } from '../../context/ApiContext';
import EditDetails from './EditDetails';
import ReactivateAccount from './ReactivateAccount/ReactivateAccount';
import { DeactivateIDSAccount } from './DeactivateIDSAccount/DeactivateIDSAccount';
import EnableIDSModal from './EnableIDS/EnableIDSModal';
import { ReactivateIDSAccount } from './ReactivateIDSAccount/ReactivateIDSAccount';

interface IUtilitySettings extends ISiteCard {
  data: IAccountData;
  statusCode: number;
  bearerToken?: string;
  requestContext: IRequestContext;
  parentPutAxios: IAny;
}
interface IAccountData {
  data: IAccountStatus[];
}

const UtilityServiceContent = (props: IUtilitySettings) => {
  const { bearerToken, requestContext, parentPutAxios } = props;
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, statusCode } = props;
  const { data: apiData } = data;

  if (
    isObjectOrArrayAndEmpty(data) &&
    statusCode &&
    !(statusCode == apiNoContentStatusCode)
  ) {
    return (
      <ConnectionError
        title={t(d.CONNECTIONERRORTITLE)}
        message={t(d.CONNECTIONERROR)}
        onRefresh={onRefresh}
      />
    );
  }
  return (
    <div>
      <UpdatedUtilityServiceAccountTable
        data={apiData ? apiData : []}
        bearerToken={bearerToken}
        requestContext={requestContext}
        parentPutAxios={parentPutAxios}
      />
    </div>
  );
};

const UtilityServiceSettingsComponentWithLoader = withLoadingStatus(
  withErrorStatus(UtilityServiceContent)
);

const UpdatedUtilityServiceSettingsComponent = (
  props: IEnergyManagementProps
) => {
  const { bearerToken, requestContext, parentPutAxios } = props;
  const d = descriptors.energyManagement;
  const [manualAccountCounts, setManualAccountCount] = useState<number>(0);
  const [openModal, setOpenModal] = useState<boolean>(true);
  const [t] = useTranslation();

  const emaasContext = useContext(ApiContext);
  const connectorConfig = {
    name: 'utility-account-get-status',
    options: {
      pageNumber: '1'
    }
  };
  const [state, dispatch] = useUtilityAccountContext();
  const isMounted = useMounted();

  const {
    showConversionModal,
    selectedUtility,
    editDetailsModal,
    showEnableIDSModal
  } = state;
  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  useEffect(() => {
    if (
      isMounted &&
      state.showModal === false &&
      state.showMeterNameChangeModal === false
    ) {
      dataConnector && dataConnector.actions && dataConnector.actions.getAll();
    }
  }, [requestContext, state]);
  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;

  const utilityAccounts = (all as unknown) as IAccountData;
  const isDataAvailable = utilityAccounts ? true : false;
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState(([] as unknown) as IAccountData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(utilityAccounts);
        const manualAccounts =
          utilityAccounts && utilityAccounts.data && utilityAccounts.data.length
            ? utilityAccounts.data.filter(
                (accn) =>
                  accn.integrationType === 'Manual' && accn.status === 'Active'
              )
            : [];
        setManualAccountCount(
          manualAccounts.filter((accn) =>
            ['Electricity', 'Gas'].includes(accn.utilityType)
          ).length
        );
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  const showModal = () => dispatch(UtilityAccountAction.showUtilityModal());
  return (
    <div className="emaas-utility-account-settings">
      <div className="utility-account-section">
        <div>
          <div className="utility-account-header">
            <div className="utility-account-title">{t(d.UTILITYSERVICES)}</div>
          </div>
          <div className="utility-account-content">
            <span>{t(d.UTILITYSERVICESDETAILS)}</span>
            {emaasContext &&
              emaasContext.utilityBuilderManualToAutomaticToggle &&
              manualAccountCounts > 0 &&
              openModal && (
                <Notification
                  hasIcon={true}
                  title=""
                  severity="information"
                  onCloseClick={() => {
                    setOpenModal(false);
                  }}
                >
                  {t(d.UtilityHandoffNotification, {
                    manualCount: manualAccountCounts
                  })}
                </Notification>
              )}

            {requestContext && requestContext.isEditAllowed ? (
              !getUtilityTypeCount(data) ? (
                <span
                  className="utility-account-right-content"
                  onClick={showModal}
                >
                  <Icon root="common" name="badge-plus" size="medium" />
                  <span className="AddUtilityService">
                    {t(d.ADDUTILITYSERVICE)}
                  </span>
                </span>
              ) : (
                <span className="utility-account-right-content-desable">
                  <Icon root="common" name="badge-plus" size="medium" />
                  <span className="AddUtilityService">
                    {t(d.ADDUTILITYSERVICE)}
                  </span>
                </span>
              )
            ) : (
              <span className="utility-account-right-content-desable">
                <Icon root="common" name="badge-plus" size="medium" />
                <span className="AddUtilityService">
                  {t(d.ADDUTILITYSERVICE)}
                </span>
              </span>
            )}
          </div>
        </div>
        <Card.Content className="utility-account-table-section">
          <UtilityServiceSettingsComponentWithLoader
            loading={isLoading}
            loadingText={t(d.LOADING)}
            data={data}
            hideNoData={true}
            onRefresh={onRefresh}
            title={t(d.CONNECTIONERRORTITLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            statusCode={statusCode!}
            error={errorInfo}
            noDataMessage={''}
            bearerToken={bearerToken}
            requestContext={requestContext}
            parentPutAxios={parentPutAxios}
          />
        </Card.Content>
        {/* <UtilityServiceModal
          requestContext={requestContext}
          bearerToken={props.bearerToken}
          utilityType={getUtilityTypeDetails(data)}
        /> */}
        <UpdatedUtilityServiceModal
          requestContext={requestContext}
          bearerToken={props.bearerToken}
          utilityType={getUtilityTypeDetails(data)}
        />
        {showConversionModal && (
          <ConvertIntrgrationModal
            requestContext={requestContext}
            bearerToken={props.bearerToken}
            utilityType={getUtilityTypeDetails(data)}
          />
        )}
        {editDetailsModal && (
          <EditDetails
            requestContext={requestContext}
            bearerToken={props.bearerToken}
            utilityType={getUtilityTypeDetails(data)}
          />
        )}

        <DeactivateAccount
          requestContext={requestContext}
          bearerToken={props.bearerToken}
        />
        <DeactivateIDSAccount
          requestContext={requestContext}
          bearerToken={props.bearerToken}
        />
        <ReactivateAccount
          requestContext={requestContext}
          bearerToken={props.bearerToken}
        />
        <ReactivateIDSAccount
          requestContext={requestContext}
          bearerToken={props.bearerToken}
        />
        <Reverify
          requestContext={requestContext}
          bearerToken={props.bearerToken}
          parentPutAxios={parentPutAxios}
        />
        <MeterNameChange
          requestContext={requestContext}
          bearerToken={props.bearerToken}
        />
        {showEnableIDSModal && (
          <EnableIDSModal
            requestContext={requestContext}
            bearerToken={props.bearerToken}
            parentPutAxios={parentPutAxios}
            utilityType={getUtilityTypeDetails(data)}
          />
        )}
      </div>
    </div>
  );
};
export default UpdatedUtilityServiceSettingsComponent;
